import type { AxiosError } from 'axios';
import type { ApiValidationIssue, ErrorResponse } from '@/types/frontend-types';

enum ApiHttpStatus {
  NetworkFailure = 0,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
}

const UNAUTHORIZED_ERROR_NAME = 'Unauthorized';
const FORBIDDEN_ERROR_NAME = 'Forbidden';
const GENERIC_ERROR_NAME = 'Error';

interface ApiErrorFactoryInput {
  message: string;
  code?: string;
  userMessage?: string;
  details?: { issues?: ApiValidationIssue[] };
}

type ErrorAxiosResponse = NonNullable<AxiosError<ErrorResponse>['response']>;
type ApiErrorFactory = (input: ApiErrorFactoryInput) => ApiError;

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string,
    public code?: string,
    public userMessage?: string,
    public details?: { issues?: ApiValidationIssue[] },
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(
    message = UNAUTHORIZED_ERROR_NAME,
    code?: string,
    userMessage?: string,
    details?: { issues?: ApiValidationIssue[] },
  ) {
    super(
      ApiHttpStatus.Unauthorized,
      UNAUTHORIZED_ERROR_NAME,
      message,
      code,
      userMessage,
      details,
    );
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(
    message = 'Access denied to this resource',
    code?: string,
    userMessage?: string,
    details?: { issues?: ApiValidationIssue[] },
  ) {
    super(
      ApiHttpStatus.Forbidden,
      FORBIDDEN_ERROR_NAME,
      message,
      code,
      userMessage,
      details,
    );
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends ApiError {
  constructor(
    message = 'Resource not found',
    code?: string,
    userMessage?: string,
    details?: { issues?: ApiValidationIssue[] },
  ) {
    super(
      ApiHttpStatus.NotFound,
      'Not Found',
      message,
      code,
      userMessage,
      details,
    );
    this.name = 'NotFoundError';
  }
}

const API_ERROR_FACTORY_BY_STATUS: Partial<Record<number, ApiErrorFactory>> = {
  [ApiHttpStatus.Unauthorized]: (input) =>
    new UnauthorizedError(
      input.message,
      input.code,
      input.userMessage,
      input.details,
    ),
  [ApiHttpStatus.Forbidden]: (input) =>
    new ForbiddenError(
      input.message,
      input.code,
      input.userMessage,
      input.details,
    ),
  [ApiHttpStatus.NotFound]: (input) =>
    new NotFoundError(
      input.message,
      input.code,
      input.userMessage,
      input.details,
    ),
};

function getApiErrorMessage(errorData: ErrorResponse): string {
  return errorData.message || errorData.error || 'Unknown error';
}

function getApiErrorDetails(errorData?: ErrorResponse): ApiErrorFactoryInput {
  if (!errorData) {
    return {
      message: 'Unknown error',
    };
  }

  return {
    message: getApiErrorMessage(errorData),
    code: errorData.code,
    userMessage: errorData.userMessage,
    details: errorData.details,
  };
}

function createGenericApiError(input: {
  status: number;
  errorData?: ErrorResponse;
  details: ApiErrorFactoryInput;
}): ApiError {
  return new ApiError(
    input.status,
    input.errorData?.error || GENERIC_ERROR_NAME,
    input.details.message,
    input.details.code,
    input.details.userMessage,
    input.details.details,
  );
}

function createResponseApiError(response: ErrorAxiosResponse): ApiError {
  const status = response.status;
  const errorData = response.data;
  const details = getApiErrorDetails(errorData);
  const factory = API_ERROR_FACTORY_BY_STATUS[status];

  if (factory) {
    return factory(details);
  }

  return createGenericApiError({ status, errorData, details });
}

function createTransportApiError(error: AxiosError<ErrorResponse>): ApiError {
  if (error.request) {
    return new ApiError(
      ApiHttpStatus.NetworkFailure,
      'Network Error',
      'No response from server',
    );
  }

  return new ApiError(
    ApiHttpStatus.NetworkFailure,
    'Request Error',
    error.message,
  );
}

export function handleAxiosError(error: AxiosError<ErrorResponse>): never {
  if (error.response) {
    throw createResponseApiError(error.response);
  }

  throw createTransportApiError(error);
}
